import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Bell, CheckCheck, Inbox } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function NotificationsPopover({ unreadInitial = 0, onChange }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(unreadInitial);

  useEffect(() => { setUnread(unreadInitial); }, [unreadInitial]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/patient/notifications");
      setNotes(data);
      const u = data.filter((n) => !n.read).length;
      setUnread(u);
      onChange?.(u);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const markAll = async () => {
    await api.post("/patient/notifications/read-all");
    await load();
  };

  const markOne = async (id) => {
    await api.post(`/patient/notifications/${id}/read`);
    await load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="topbar-notif-btn"
          className="relative h-9 w-9 rounded-full hover:bg-[#E2E8F0] flex items-center justify-center"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4 text-mr-primary" />
          {unread > 0 && (
            <span data-testid="topbar-notif-badge" className="absolute -top-0.5 -right-0.5 bg-mr-accent text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center">{unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0 rounded-2xl overflow-hidden" data-testid="notif-popover">
        <div className="px-4 py-3 border-b border-mr flex items-center justify-between bg-mr-bg">
          <p className="font-display font-semibold text-mr-primary">Notifications</p>
          {notes.some((n) => !n.read) && (
            <button data-testid="notif-mark-all" onClick={markAll} className="text-xs text-mr-accent hover:underline inline-flex items-center gap-1">
              <CheckCheck className="h-3 w-3" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <p className="p-8 text-center text-mr-secondary text-sm">Loading…</p>
          ) : notes.length === 0 ? (
            <div className="p-10 text-center">
              <Inbox className="h-7 w-7 mx-auto text-mr-secondary mb-2" />
              <p className="font-display text-mr-primary text-sm">You're all caught up</p>
              <p className="text-xs text-mr-secondary mt-1">We'll notify you of points, rewards & visits.</p>
            </div>
          ) : (
            <ul className="divide-y divide-mr-border">
              {notes.map((n) => (
                <li
                  key={n.id}
                  data-testid={`notif-item-${n.id}`}
                  onClick={() => !n.read && markOne(n.id)}
                  className={`p-3 cursor-pointer hover:bg-mr-bg transition-colors ${!n.read ? "bg-mr-bg/60" : ""}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-mr-accent flex-shrink-0" />}
                    <div className={`flex-1 ${n.read ? "ml-4" : ""}`}>
                      <p className="text-sm font-medium text-mr-primary leading-snug">{n.title}</p>
                      <p className="text-xs text-mr-secondary mt-0.5 leading-snug">{n.body}</p>
                      <p className="text-[10px] text-mr-secondary mt-1.5 uppercase tracking-wide">{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
